import { createError } from '@common/neo.tax';

import {
  advancedFilter,
  Conditions,
  FilterField,
  GetPayrollQresQuery,
  GetPayrollQresQueryVariables,
  GET_PAYROLL_QRES,
  keywordFilter,
  PayrollQreFilter,
  PayrollQreSort,
  useCursorPaginatedQuery,
  useSticky,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

export type PayrollQreFilterableColumn = FilterField<PayrollQreFilter>;

export type PayrollQre = NonNullable<GetPayrollQresQuery['getPayrollQres']['edges']>[number]['node'];

export type WithPayrollExpense<T extends PayrollQre> = Omit<T, 'payrollExpenseId'> & {
  payrollExpenseId: string;
};

export const isPayrollQreWithPayrollExpense = (qre: PayrollQre): qre is WithPayrollExpense<PayrollQre> =>
  qre.payrollExpenseId != null;

const GET_PAYROLL_QRES_KEY = 'GET_PAYROLL_QRES_KEY';

interface UsePayrollQresProps {
  filterConditions?: Conditions<PayrollQreFilterableColumn>;
  searchKeyword?: string | null;
  sorting?: Array<PayrollQreSort> | PayrollQreSort;
}

export const usePayrollQres = ({ filterConditions, searchKeyword, sorting }: UsePayrollQresProps = {}): {
  loading: boolean;
  payrollQres: PayrollQre[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetPayrollQresQuery, GetPayrollQresQueryVariables>(
    GET_PAYROLL_QRES,
    {
      variables: {
        fiscalYearId,
        filter: {
          and: [
            keywordFilter(['name', 'email', 'title'], searchKeyword),
            advancedFilter<PayrollQreFilter>(filterConditions),
          ],
        },
        sorting,
      },
      context: {
        debounceKey: GET_PAYROLL_QRES_KEY,
      },
    },
    {
      getConnection: (data) => data.getPayrollQres,
      numElementsPerFetch: 40,
    },
  );
  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const payrollQres = stickyData?.getPayrollQres?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      payrollQres: payrollQres,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    throw createError('MissingPayrollQres', 'No data returned from getPayrollQres query');
  }

  return {
    loading,
    payrollQres,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
